import { useState, useEffect } from 'react';
import {
  Database, Plus, Trash2, Edit3, X
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';

// ─── Types ──────────────────────────────────────────

interface DataFieldMapping {
  id: string;
  sourceFieldId: string;
  targetFieldId: string;
  aggregationMode: 'OVERWRITE' | 'SUM';
  lateDataHandling: 'PROCESS' | 'DISCARD' | 'MARK_LATE';
  sourceField: { id: string; name: string; type: string; organizationId: string };
  targetField: { id: string; name: string; type: string; organizationId: string };
}

interface DataField {
  id: string;
  name: string;
  type: string;
  aggregationMode: string;
}

const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors';
const labelClass = 'text-sm text-gray-700 block mb-1';

export default function DataSourcesPage() {
  const { user } = useAuth();
  const orgId = user?.organizationId || '';

  const [mappings, setMappings] = useState<DataFieldMapping[]>([]);
  const [fields, setFields] = useState<DataField[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    sourceFieldId: '',
    targetFieldId: '',
    aggregationMode: 'OVERWRITE' as 'OVERWRITE' | 'SUM',
    lateDataHandling: 'PROCESS' as 'PROCESS' | 'DISCARD' | 'MARK_LATE',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!orgId) return;
    loadData();
  }, [orgId]);

  async function loadData() {
    setLoading(true);
    try {
      const [mapRes, fieldRes] = await Promise.all([
        api.get(`/data-sources?organizationId=${orgId}`),
        api.get(`/data-sources/fields?organizationId=${orgId}`),
      ]);
      setMappings(mapRes.data || []);
      setFields(fieldRes.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }

  function openCreate() {
    setForm({ sourceFieldId: '', targetFieldId: '', aggregationMode: 'OVERWRITE', lateDataHandling: 'PROCESS' });
    setEditingId(null);
    setShowForm(true);
    setError('');
  }

  function openEdit(m: DataFieldMapping) {
    setForm({
      sourceFieldId: m.sourceFieldId,
      targetFieldId: m.targetFieldId,
      aggregationMode: m.aggregationMode,
      lateDataHandling: m.lateDataHandling,
    });
    setEditingId(m.id);
    setShowForm(true);
    setError('');
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (form.sourceFieldId === form.targetFieldId) {
      setError('Quell- und Zielfeld müssen unterschiedlich sein');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/data-sources/${editingId}`, {
          aggregationMode: form.aggregationMode,
          lateDataHandling: form.lateDataHandling,
        });
      } else {
        await api.post('/data-sources', form);
      }
      setShowForm(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Mapping wirklich löschen?')) return;
    await api.delete(`/data-sources/${id}`);
    loadData();
  }

  const AGG_LABELS: Record<string, string> = {
    OVERWRITE: 'Überschreiben',
    SUM: 'Summe',
  };

  const LATE_LABELS: Record<string, string> = {
    PROCESS: 'Verarbeiten',
    DISCARD: 'Verwerfen',
    MARK_LATE: 'Markieren',
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Lade...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Verarbeitungslogik & Aggregation</h1>
            <p className="text-sm text-gray-500">Mapping-Konfiguration zwischen Hierarchieebenen</p>
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={16} /> Mapping
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{editingId ? 'Mapping bearbeiten' : 'Neues Verarbeitungslogik-Mapping'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              {!editingId && (
                <>
                  <div>
                    <label className={labelClass}>Quell-Datenfeld *</label>
                    <select className={inputClass} value={form.sourceFieldId} onChange={(e) => setForm({ ...form, sourceFieldId: e.target.value })} required>
                      <option value="">— Quelle wählen —</option>
                      {fields.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.type})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Ziel-Datenfeld *</label>
                    <select className={inputClass} value={form.targetFieldId} onChange={(e) => setForm({ ...form, targetFieldId: e.target.value })} required>
                      <option value="">— Ziel wählen —</option>
                      {fields.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.type})</option>)}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className={labelClass}>Aggregations-Modus</label>
                <select className={inputClass} value={form.aggregationMode} onChange={(e) => setForm({ ...form, aggregationMode: e.target.value as any })}>
                  <option value="OVERWRITE">Überschreiben (OVERWRITE)</option>
                  <option value="SUM">Summe (SUM)</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Late-Data-Handling</label>
                <select className={inputClass} value={form.lateDataHandling} onChange={(e) => setForm({ ...form, lateDataHandling: e.target.value as any })}>
                  <option value="PROCESS">Verarbeiten (PROCESS)</option>
                  <option value="DISCARD">Verwerfen (DISCARD)</option>
                  <option value="MARK_LATE">Als verspätet markieren (MARK_LATE)</option>
                </select>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">Abbrechen</button>
                <button type="submit" className="btn-primary text-sm">{editingId ? 'Speichern' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mapping List */}
      {mappings.length === 0 ? (
        <div className="card text-center py-12">
          <Database size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Keine Verarbeitungslogik-Mappings konfiguriert.</p>
          <p className="text-xs text-gray-400 mt-1">Erstelle ein Mapping um Datenfelder zwischen Hierarchieebenen zu verknüpfen.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {mappings.map((m) => (
            <div key={m.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  {/* Source */}
                  <div className="bg-blue-50 rounded-lg px-3 py-2 min-w-[180px]">
                    <div className="text-xs text-blue-500 mb-0.5">Quelle</div>
                    <div className="font-medium text-gray-900 text-sm">{m.sourceField.name}</div>
                    <div className="text-xs text-gray-400">{m.sourceField.type}</div>
                  </div>
                  {/* Arrow + Mode */}
                  <div className="flex flex-col items-center justify-center pt-2">
                    <span className="text-gray-300 text-lg">→</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded mt-1 ${m.aggregationMode === 'SUM' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {AGG_LABELS[m.aggregationMode]}
                    </span>
                  </div>
                  {/* Target */}
                  <div className="bg-green-50 rounded-lg px-3 py-2 min-w-[180px]">
                    <div className="text-xs text-green-500 mb-0.5">Ziel</div>
                    <div className="font-medium text-gray-900 text-sm">{m.targetField.name}</div>
                    <div className="text-xs text-gray-400">{m.targetField.type}</div>
                  </div>
                  {/* Late Data */}
                  <div className="pt-3">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${m.lateDataHandling === 'PROCESS' ? 'bg-blue-50 text-blue-600' : m.lateDataHandling === 'DISCARD' ? 'bg-red-50 text-red-600' : 'bg-yellow-50 text-yellow-600'}`}>
                      {LATE_LABELS[m.lateDataHandling]}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button onClick={() => openEdit(m)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Bearbeiten"><Edit3 size={15} /></button>
                  <button onClick={() => handleDelete(m.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Löschen"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
